import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { getBaseUrl } from './ApiContext';
import { useAuth } from './AuthContext';
import LoadingIndicator from './LoadingIndicator';
import './Portfolio.css';

const baseUrl = getBaseUrl();

// Display order and labels for the reconciler's schema
// (data_modules/introductions/portfolio_profile.py METRIC_FIELDS). Kept in
// the order a partner reads them -- top line first, cash last -- rather
// than alphabetically, which buries ARR under ACV.
const FIELDS = [
    ['arr', 'ARR'],
    ['revenue', 'Revenue'],
    ['growth', 'Growth'],
    ['customers', 'Customers'],
    ['acv', 'ACV'],
    ['pipeline', 'Pipeline'],
    ['conversion', 'Conversion'],
    ['retention', 'Retention'],
    ['burn', 'Burn'],
    ['runway_months', 'Runway'],
    ['cash_balance', 'Cash on hand'],
    ['cash_raised', 'Raised'],
    ['valuation', 'Valuation'],
    ['profitability', 'Profitability'],
    ['headcount', 'Headcount'],
];
const LABELS = Object.fromEntries(FIELDS);
const ORDER = FIELDS.map(([key]) => key);

const FILTERS = [
    ['metrics', 'Metrics on record', (c) => Object.keys(c.metrics).length > 0],
    ['targets', 'Named a target', (c) => (c.named_targets || []).some((t) => t.status !== 'handled')],
    ['nometrics', 'Data, no metrics', (c) => c.sources > 0 && !Object.keys(c.metrics).length],
    ['silent', 'Nothing heard', (c) => !c.sources],
    ['all', 'All', () => true],
];

function formatDay(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Gmail links in the LP tool's form (lp_outreach/outreach/drafter.py,
// gmail_thread_url + _with_authuser). Stored links carry no account, or
// /u/0/ on rows from before 2026-09-11, which names a browser slot rather
// than an account: for anyone signed into two Google accounts it opens the
// wrong inbox and finds nothing. Strip the slot, open in the viewer's own.
export function gmailFor(url, email) {
    if (!url || !url.startsWith('https://mail.google.com/mail/')) return url;
    const clean = url.replace(/^(https:\/\/mail\.google\.com\/mail\/)u\/\d+\//, '$1');
    if (!email || clean.includes('authuser=')) return clean;
    const hash = clean.indexOf('#');
    const base = hash === -1 ? clean : clean.slice(0, hash);
    const fragment = hash === -1 ? '' : clean.slice(hash);
    return `${base}${base.includes('?') ? '&' : '?'}authuser=${email}${fragment}`;
}

function CompanyCard({ company, expanded, onToggle, email }) {
    const metricKeys = Object.keys(company.metrics)
        .sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    // Defaulted rather than assumed: the API and this bundle deploy
    // separately, so a build can be live against a backend that predates
    // the needs columns. An absent section is the correct rendering of
    // "the pipeline has not written this yet" either way.
    const icp = company.icp || null;
    const targets = company.named_targets || [];
    const risks = company.risks || [];
    // The count on the closed card is what is still to be done. A handled
    // target is on the record so nobody asks twice, not work waiting.
    const openTargets = targets.filter((t) => t.status !== 'handled');

    return (
        <div className="pcard">
            <div
                className="pcard-head"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
            >
                <span className="pcard-caret">{expanded ? '▾' : '▸'}</span>
                <span className="pcard-name">{company.name}</span>
                <span className="pcard-desc">{company.description}</span>
                <span className="pcard-tags">
                    {metricKeys.length > 0 && (
                        <span className="ptag on">
                            {metricKeys.length} metric{metricKeys.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {company.asks.length > 0 && (
                        <span className="ptag">
                            {company.asks.length} ask{company.asks.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {openTargets.length > 0 && (
                        <span className="ptag">
                            {openTargets.length} target{openTargets.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {risks.length > 0 && (
                        <span className="ptag warn">
                            {risks.length} risk{risks.length > 1 ? 's' : ''}
                        </span>
                    )}
                    {company.sources > 0 ? (
                        <span className="ptag">{company.sources} src</span>
                    ) : (
                        <span className="ptag warn">nothing heard</span>
                    )}
                </span>
            </div>

            {expanded && (
                <div className="pcard-body">
                    {metricKeys.length > 0 && (
                        <>
                            <div className="psec">Current metrics</div>
                            <table className="pmetrics">
                                <tbody>
                                    {metricKeys.map((key) => {
                                        const m = company.metrics[key];
                                        return (
                                            <tr key={key}>
                                                <td className="pm-field">{LABELS[key] || key}</td>
                                                <td>
                                                    <span className="pm-value">{m.value}</span>
                                                    {m.basis !== 'actual' && (
                                                        <span className={`pbasis ${m.basis}`}>{m.basis}</span>
                                                    )}
                                                    {m.previous && (
                                                        <span className="pm-prev">
                                                            was {m.previous.value}
                                                            {m.previous.observed_on ? ` ${m.previous.observed_on}` : ''}
                                                        </span>
                                                    )}
                                                    <div className="pm-quote">{m.statement}</div>
                                                </td>
                                                <td className="pm-source">
                                                    {m.observed_on}
                                                    <br />
                                                    {/* Every figure opens the exact message or note it
                                                        came from -- the reconciler stores source_url per
                                                        metric precisely so this needs no lookup. */}
                                                    {m.source_url && (
                                                        <a href={gmailFor(m.source_url, email)} target="_blank" rel="noopener noreferrer">
                                                            {m.source === 'granola' ? 'call note' : 'email'}
                                                            {' '}
                                                            <FaExternalLinkAlt size={9} />
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {company.runway_notes && (
                        <>
                            <div className="psec">Runway</div>
                            <div className="prunway">{company.runway_notes}</div>
                        </>
                    )}

                    {company.asks.length > 0 && (
                        <>
                            <div className="psec">Open asks</div>
                            {company.asks.map((ask, i) => (
                                <div className="pask" key={i}>
                                    <span className="when">{ask.last_raised}</span>
                                    <span>
                                        {ask.ask}
                                        {ask.times_raised > 1 ? ` (×${ask.times_raised})` : ''}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}

                    {icp && (
                        <>
                            <div className="psec">Who buys it</div>
                            <div className="picp">
                                {icp.what_they_sell && (
                                    <div className="picp-sells">{icp.what_they_sell}</div>
                                )}
                                {icp.buyer_roles?.length > 0 && (
                                    <div className="picp-row">
                                        <span className="picp-label">Buyers</span>
                                        {icp.buyer_roles.map((role, i) => (
                                            <span className="pchip" key={i}>{role}</span>
                                        ))}
                                    </div>
                                )}
                                {icp.target_segments?.length > 0 && (
                                    <div className="picp-row">
                                        <span className="picp-label">Segments</span>
                                        {icp.target_segments.map((segment, i) => (
                                            <span className="pchip" key={i}>{segment}</span>
                                        ))}
                                    </div>
                                )}
                                {/* Every profile on the page cites the lines it
                                    was built from -- an ICP with no evidence is
                                    refused by the pipeline, so this list is
                                    never empty for a profile that exists. */}
                                {icp.evidence?.length > 0 && (
                                    <div className="picp-evidence">
                                        {icp.evidence.map((e, i) => (
                                            <div key={i}>
                                                {e.source_url ? (
                                                    <a href={gmailFor(e.source_url, email)} target="_blank" rel="noopener noreferrer">
                                                        {e.statement}
                                                    </a>
                                                ) : e.statement}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {targets.length > 0 && (
                        <>
                            <div className="psec">Named targets</div>
                            {targets.map((target, i) => (
                                <div className={`ptarget ${target.status}`} key={i}>
                                    <span className="when">{target.last_requested}</span>
                                    <span>
                                        <span className="pt-name">{target.name}</span>
                                        {target.at ? <span className="pt-at"> at {target.at}</span> : ''}
                                        <span className="pt-kind">{target.kind}</span>
                                        {target.status === 'handled' && (
                                            <span className="pt-done">✓ introduced</span>
                                        )}
                                        {target.times_requested > 1 ? ` ×${target.times_requested}` : ''}
                                        <div className="pm-quote">
                                            {target.source_url ? (
                                                <a href={gmailFor(target.source_url, email)} target="_blank" rel="noopener noreferrer">
                                                    {target.ask}
                                                </a>
                                            ) : target.ask}
                                        </div>
                                    </span>
                                </div>
                            ))}
                        </>
                    )}

                    {risks.length > 0 && (
                        <>
                            <div className="psec">Open risks</div>
                            {risks.map((risk, i) => (
                                <div className="prisk" key={i}>
                                    <span className="when">{risk.last_raised}</span>
                                    <span>
                                        {risk.source_url ? (
                                            <a href={gmailFor(risk.source_url, email)} target="_blank" rel="noopener noreferrer">
                                                {risk.risk}
                                            </a>
                                        ) : risk.risk}
                                        {risk.times_raised > 1 ? ` (×${risk.times_raised})` : ''}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}

                    {company.rejected.length > 0 && (
                        <>
                            <div className="psec">Ruled not a metric</div>
                            {company.rejected.map((r, i) => (
                                <div className="prej" key={i}>
                                    {r.override && <span className="marker">✎ </span>}
                                    {r.statement}
                                </div>
                            ))}
                        </>
                    )}

                    {company.sources === 0 && (
                        <div className="pempty">
                            {company.domain
                                ? `Detection is live on ${company.domain}, but no call or email from them has been found.`
                                : 'No domain set, so nothing can be detected for this company.'}
                        </div>
                    )}
                    {company.sources > 0 && metricKeys.length === 0 && (
                        <div className="pempty">
                            {company.sources} source record{company.sources > 1 ? 's' : ''}, but no figure stated in any of them.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Portfolio() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('metrics');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        let cancelled = false;
        axios.get(`${baseUrl}/api/portfolio`)
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((err) => {
                if (!cancelled) setError(err.response?.data?.error || err.message);
            });
        return () => { cancelled = true; };
    }, []);

    const visible = useMemo(() => {
        if (!data) return [];
        const predicate = FILTERS.find((f) => f[0] === filter)[2];
        const term = search.trim().toLowerCase();
        return data.companies
            .filter(predicate)
            .filter((c) => !term
                || c.name.toLowerCase().includes(term)
                || (c.description || '').toLowerCase().includes(term))
            .sort((a, b) => Object.keys(b.metrics).length - Object.keys(a.metrics).length
                || b.sources - a.sources
                || a.name.localeCompare(b.name));
    }, [data, filter, search]);

    if (error) {
        return <div className="portfolio"><div className="pempty">Could not load portfolio: {error}</div></div>;
    }
    if (!data) {
        return <div className="portfolio"><LoadingIndicator /></div>;
    }

    const lastBuilt = data.companies
        .map((c) => c.updated_at)
        .filter(Boolean)
        .sort()
        .pop();

    return (
        <div className="portfolio">
            <div className="portfolio-header">
                <div className="portfolio-title">
                    <h1>Portfolio</h1>
                    <p>
                        Reconciled from calls and investor email
                        {lastBuilt ? ` · last built ${formatDay(lastBuilt)}` : ''}
                    </p>
                </div>
                <div className="portfolio-stats">
                    <div className="pstat">
                        <div className="n">{data.totals.companies}</div>
                        <div className="l">Core deals</div>
                    </div>
                    <div className="pstat">
                        <div className="n">{data.totals.with_data}</div>
                        <div className="l">Data flowing</div>
                    </div>
                    <div className="pstat">
                        <div className="n">{data.totals.with_metrics}</div>
                        <div className="l">With metrics</div>
                    </div>
                    <div className="pstat">
                        <div className="n">{data.totals.metric_fields}</div>
                        <div className="l">Fields on record</div>
                    </div>
                    <div className="pstat">
                        <div className="n">{data.totals.open_targets ?? 0}</div>
                        <div className="l">Named targets</div>
                    </div>
                </div>
            </div>

            <div className="portfolio-controls">
                <div className="search-wrap">
                    <FaSearch />
                    <input
                        type="text"
                        placeholder="Search companies..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {FILTERS.map(([key, label, predicate]) => (
                    <button
                        key={key}
                        className={`pfilter ${filter === key ? 'active' : ''}`}
                        onClick={() => setFilter(key)}
                    >
                        {label} · {data.companies.filter(predicate).length}
                    </button>
                ))}
            </div>

            <div className="portfolio-list">
                {visible.map((company) => (
                    <CompanyCard
                        key={company.id}
                        company={company}
                        email={user?.email}
                        expanded={!!expanded[company.id]}
                        onToggle={() => setExpanded((prev) => ({
                            ...prev, [company.id]: !prev[company.id],
                        }))}
                    />
                ))}
                {visible.length === 0 && (
                    <div className="pempty">No companies match.</div>
                )}
            </div>

            <div className="portfolio-footnote">
                Every figure, target and risk links to the email or call note it came from. ✎ marks a ruling made by hand.
            </div>
        </div>
    );
}

export default Portfolio;
